import { parseISO } from "date-fns";
import { z } from "zod";

export const WebsiteShortNameSchema = z.string()
  .min(3, "Use at least 3 characters.")
  .max(40, "Use no more than 40 characters.")
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Use lowercase letters, numbers and single hyphens between words.")
  .refine(value => !["admin", "api", "book", "help", "login", "www"].includes(value), "This short name is reserved. Choose another business short name.");

const BusinessWebsiteDtoSchema = z.object({
  business_id: z.string(),
  short_name: WebsiteShortNameSchema,
  enabled: z.boolean(),
  description: z.string().nullable(),
  logo_src: z.string().nullable(),
  created_at: z.string(),
  modified_at: z.string(),
});

const BusinessWebsiteSchema = z.object({
  businessId: z.string(),
  shortName: WebsiteShortNameSchema,
  enabled: z.boolean(),
  description: z.string().nullable(),
  logoSrc: z.string().nullable(),
  createdAt: z.date(),
  modifiedAt: z.date(),
});

export const UpdateBusinessWebsiteDtoSchema = BusinessWebsiteDtoSchema.omit({
  business_id: true,
  created_at: true,
  modified_at: true,
}).extend({ description: z.string().max(300).nullable() }).partial();

export const UpdateBusinessWebsiteRequestSchema = BusinessWebsiteSchema.omit({
  businessId: true,
  createdAt: true,
  modifiedAt: true,
}).extend({ description: z.string().max(300).nullable() }).partial();

export const WebsiteLogoUploadDtoSchema = z.object({
  upload_url: z.url(),
  logo_src: z.url(),
});

export type BusinessWebsiteDto = z.infer<typeof BusinessWebsiteDtoSchema>;
export type UpdateBusinessWebsiteDto = z.infer<typeof UpdateBusinessWebsiteDtoSchema>;
export type WebsiteLogoUploadDto = z.infer<typeof WebsiteLogoUploadDtoSchema>;
export type BusinessWebsite = z.infer<typeof BusinessWebsiteSchema>;
export type UpdateBusinessWebsiteRequest = z.infer<typeof UpdateBusinessWebsiteRequestSchema>;

export function fromBusinessWebsiteDtoToBusinessWebsite(dto: BusinessWebsiteDto): BusinessWebsite {
  const websiteDto = BusinessWebsiteDtoSchema.parse(dto);
  return {
    businessId: websiteDto.business_id,
    shortName: websiteDto.short_name,
    enabled: websiteDto.enabled,
    description: websiteDto.description,
    logoSrc: websiteDto.logo_src,
    createdAt: parseISO(websiteDto.created_at),
    modifiedAt: parseISO(websiteDto.modified_at),
  };
}

export function fromUpdateBusinessWebsiteRequestToUpdateBusinessWebsiteDto(
  request: UpdateBusinessWebsiteRequest,
): UpdateBusinessWebsiteDto {
  const websiteRequest = UpdateBusinessWebsiteRequestSchema.parse(request);
  return {
    short_name: websiteRequest.shortName,
    enabled: websiteRequest.enabled,
    description: websiteRequest.description,
    logo_src: websiteRequest.logoSrc,
  };
}
